import mammoth from 'mammoth';
import extractPdfText from 'react-pdftotext';
import * as XLSX from 'xlsx';
import { strFromU8, unzipSync } from 'fflate';
import { Attachment } from '@/types';
import { generateId } from '@/lib/utils';

const TEXT_EXTENSIONS = new Set([
  'txt',
  'md',
  'markdown',
  'json',
  'csv',
  'ts',
  'tsx',
  'js',
  'jsx',
  'css',
  'html',
  'htm',
  'xml',
  'yml',
  'yaml',
  'py',
  'java',
  'c',
  'cpp',
  'h',
  'hpp',
  'rs',
  'go',
  'sql',
  'sh',
  'bat',
  'ps1',
  'log',
  'ini',
  'toml',
]);

const SPREADSHEET_EXTENSIONS = new Set(['xls', 'xlsx', 'ods']);
const PRESENTATION_EXTENSIONS = new Set(['ppt', 'pptx', 'odp']);
const WORD_PROCESSOR_EXTENSIONS = new Set(['doc', 'docx', 'odt', 'rtf']);

const formatFileSize = (size: number) => `${(size / 1024).toFixed(2)} KB`;

const getFileExtension = (fileName: string) => fileName.split('.').pop()?.toLowerCase() || '';

const normalizeExtractedText = (text: string) =>
  text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\u0000/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const buildBinaryPlaceholder = (file: File) => {
  const fileType = file.type || getFileExtension(file.name) || 'unknown';

  return `[This is a binary ${fileType} file: ${file.name}]\n\nFile size: ${formatFileSize(file.size)}\nFile type: ${fileType}\n\nThis content is a placeholder for the binary file. The actual file content cannot be displayed as text.`;
};

const buildExtractionFailureMessage = (file: File, label: string) => {
  const fileType = file.type || getFileExtension(file.name) || 'unknown';

  return `[This is a ${label} file: ${file.name}]\n\nFile size: ${formatFileSize(file.size)}\nFile type: ${fileType}\n\nUnable to extract readable text from this document.`;
};

const readFileAsArrayBuffer = async (file: File): Promise<ArrayBuffer> => {
  const reader = new FileReader();

  return await new Promise<ArrayBuffer>((resolve, reject) => {
    reader.onload = (event) => {
      if (event.target?.result instanceof ArrayBuffer) {
        resolve(event.target.result);
        return;
      }

      reject(new Error('Failed to read file as ArrayBuffer'));
    };

    reader.onerror = () => reject(reader.error || new Error('Failed to read file as ArrayBuffer'));
    reader.readAsArrayBuffer(file);
  });
};

const readFileAsText = async (file: File): Promise<string> => {
  const reader = new FileReader();

  return await new Promise<string>((resolve, reject) => {
    reader.onload = (event) => {
      if (typeof event.target?.result === 'string') {
        resolve(event.target.result);
        return;
      }

      reject(new Error('Failed to read file as text'));
    };

    reader.onerror = () => reject(reader.error || new Error('Failed to read file as text'));
    reader.readAsText(file);
  });
};

const extractTextFromXml = (xml: string, blockTags: Set<string>) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'application/xml');
  const parts: string[] = [];

  const visit = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const value = node.textContent?.replace(/\s+/g, ' ').trim();
      if (value) {
        parts.push(value, ' ');
      }
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const element = node as Element;
    const localName = element.localName?.toLowerCase() || '';

    if (localName === 'tab') {
      parts.push('\t');
    }

    Array.from(element.childNodes).forEach(visit);

    if (blockTags.has(localName)) {
      parts.push('\n');
    }
  };

  visit(doc.documentElement);
  return normalizeExtractedText(parts.join(''));
};

const extractZipXmlText = (arrayBuffer: ArrayBuffer, entryMatcher: RegExp, blockTags: Set<string>) => {
  const archive = unzipSync(new Uint8Array(arrayBuffer));
  const entries = Object.keys(archive)
    .filter((entry) => entryMatcher.test(entry))
    .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));

  const text = entries
    .map((entry) => extractTextFromXml(strFromU8(archive[entry], true), blockTags))
    .filter(Boolean)
    .join('\n\n');

  return normalizeExtractedText(text);
};

const extractDocxText = async (arrayBuffer: ArrayBuffer) => {
  const result = await mammoth.extractRawText({ arrayBuffer });
  return normalizeExtractedText(result.value || '');
};

const extractPdfDocumentText = async (arrayBuffer: ArrayBuffer) => {
  const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
  const text = await extractPdfText(blob);
  return normalizeExtractedText(text || '');
};

const extractSpreadsheetText = (arrayBuffer: ArrayBuffer) => {
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const text = workbook.SheetNames.map((sheetName) => {
    const sheet = workbook.Sheets[sheetName];
    const csv = XLSX.utils.sheet_to_csv(sheet, { blankrows: false }).trim();
    return csv ? `# Sheet: ${sheetName}\n${csv}` : '';
  })
    .filter(Boolean)
    .join('\n\n');

  return normalizeExtractedText(text);
};

const decodeRtfHex = (value: string) =>
  value.replace(/\\'([0-9a-fA-F]{2})/g, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)));

const extractRtfText = (content: string) => {
  const text = decodeRtfHex(content)
    .replace(/\\par[d]?/g, '\n')
    .replace(/\\line/g, '\n')
    .replace(/\\tab/g, '\t')
    .replace(/\\u(-?\d+)\??/g, (_, codePoint: string) => {
      const parsed = Number(codePoint);
      return Number.isNaN(parsed) ? '' : String.fromCharCode(parsed < 0 ? parsed + 65536 : parsed);
    })
    .replace(/\\[a-z]+-?\d* ?/gi, '')
    .replace(/[{}]/g, '');

  return normalizeExtractedText(text);
};

const extractReadableSegments = (text: string) => {
  const segments = text
    .replace(/\r/g, '\n')
    .replace(/\u0000/g, ' ')
    .split(/\n+/)
    .flatMap((line) => line.split(/[\u0001-\u001F\u007F-\u009F]+/))
    .map((segment) => segment.replace(/\s+/g, ' ').trim())
    .filter((segment) => segment.length >= 4 && /[A-Za-z\u00C0-\u024F]/.test(segment));

  return Array.from(new Set(segments));
};

const extractLegacyDocText = (arrayBuffer: ArrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer);
  const utf16Text = new TextDecoder('utf-16le', { fatal: false }).decode(bytes);
  const latin1Text = new TextDecoder('latin1', { fatal: false }).decode(bytes);
  const asciiMatches = latin1Text.match(/[A-Za-z0-9][A-Za-z0-9 ,.;:'"()!?/\\\-–—&%$#@*+=\[\]{}]{3,}/g) ?? [];

  const segments = [
    ...extractReadableSegments(utf16Text),
    ...asciiMatches.map((segment) => segment.trim()),
  ].filter(Boolean);

  return normalizeExtractedText(Array.from(new Set(segments)).slice(0, 400).join('\n'));
};

const isTextFile = (file: File, extension: string) =>
  TEXT_EXTENSIONS.has(extension) ||
  file.type.startsWith('text/') ||
  file.type === 'application/json' ||
  file.type === 'application/xml';

const isPdfFile = (file: File, extension: string) =>
  file.type.includes('pdf') || extension === 'pdf';

const isSpreadsheetFile = (file: File, extension: string) =>
  SPREADSHEET_EXTENSIONS.has(extension) ||
  file.type.includes('spreadsheetml') ||
  file.type.includes('ms-excel') ||
  file.type === 'application/vnd.oasis.opendocument.spreadsheet';

const isPresentationFile = (file: File, extension: string) =>
  PRESENTATION_EXTENSIONS.has(extension) ||
  file.type.includes('presentationml') ||
  file.type.includes('ms-powerpoint') ||
  file.type === 'application/vnd.oasis.opendocument.presentation';

const isWordProcessorFile = (file: File, extension: string) =>
  WORD_PROCESSOR_EXTENSIONS.has(extension) ||
  file.type.includes('msword') ||
  file.type.includes('wordprocessingml') ||
  file.type === 'application/vnd.oasis.opendocument.text' ||
  file.type === 'text/rtf' ||
  file.type === 'application/rtf';

export const readFileContent = async (file: File): Promise<string> => {
  const extension = getFileExtension(file.name);

  try {
    if (isTextFile(file, extension)) {
      return normalizeExtractedText(await readFileAsText(file));
    }

    if (extension === 'rtf' || file.type === 'text/rtf' || file.type === 'application/rtf') {
      const text = extractRtfText(await readFileAsText(file));
      return text || buildExtractionFailureMessage(file, 'RTF');
    }

    const arrayBuffer = await readFileAsArrayBuffer(file);

    if (isPdfFile(file, extension)) {
      const text = await extractPdfDocumentText(arrayBuffer);
      return text || buildExtractionFailureMessage(file, 'PDF');
    }

    if (extension === 'docx') {
      const text = await extractDocxText(arrayBuffer);
      return text || buildExtractionFailureMessage(file, 'DOCX');
    }

    if (extension === 'doc') {
      const text = extractLegacyDocText(arrayBuffer);
      return text || buildExtractionFailureMessage(file, 'DOC');
    }

    if (extension === 'odt') {
      const text = extractZipXmlText(arrayBuffer, /^content\.xml$/i, new Set(['p', 'h', 'list-item']));
      return text || buildExtractionFailureMessage(file, 'ODT');
    }

    if (isSpreadsheetFile(file, extension)) {
      const text = extractSpreadsheetText(arrayBuffer);
      return text || buildExtractionFailureMessage(file, extension.toUpperCase() || 'spreadsheet');
    }

    if (extension === 'pptx') {
      const text = extractZipXmlText(arrayBuffer, /^ppt\/slides\/slide\d+\.xml$/i, new Set(['p']));
      return text || buildExtractionFailureMessage(file, 'PPTX');
    }

    if (extension === 'ppt') {
      const text = extractLegacyDocText(arrayBuffer);
      return text || buildExtractionFailureMessage(file, 'PPT');
    }

    if (extension === 'odp') {
      const text = extractZipXmlText(arrayBuffer, /^content\.xml$/i, new Set(['p', 'h', 'list-item']));
      return text || buildExtractionFailureMessage(file, 'ODP');
    }

    if (isWordProcessorFile(file, extension) || isPresentationFile(file, extension)) {
      const text = extractLegacyDocText(arrayBuffer);
      return text || buildExtractionFailureMessage(file, extension.toUpperCase() || 'document');
    }

    if (file.type.startsWith('image/') || file.type.startsWith('audio/')) {
      return buildBinaryPlaceholder(file);
    }

    const fallbackText = extractLegacyDocText(arrayBuffer);
    return fallbackText || buildBinaryPlaceholder(file);
  } catch (error) {
    console.error('Error reading file content:', error);
    return buildBinaryPlaceholder(file);
  }
};

export const getFileType = (file: File): Attachment['type'] => {
  if (file.type.startsWith('image/')) {
    return 'image';
  }

  if (file.type.startsWith('audio/')) {
    return 'audio';
  }

  if (
    file.type === 'text/plain' ||
    file.type === 'text/markdown' ||
    file.type === 'application/json' ||
    file.name.endsWith('.txt') ||
    file.name.endsWith('.md') ||
    file.name.endsWith('.json')
  ) {
    return 'code';
  }

  if (file.type === 'application/pdf') {
    return 'file';
  }

  return 'file';
};

export const extractCodeSnippets = (messages: any[]) => {
  const snippets: Array<{ id: string; code: string; language: string; name: string }> = [];

  messages.forEach((message) => {
    if (message.attachments && message.attachments.length > 0) {
      message.attachments.forEach((attachment: any) => {
        if (attachment.type === 'code') {
          const language = attachment.name ? attachment.name.split('.').pop() || 'text' : 'text';
          const name = message.content || `Code snippet ${snippets.length + 1}`;

          snippets.push({
            id: attachment.id,
            code: attachment.content,
            language,
            name: name.replace("I've shared a code snippet in ", '').replace(':', ''),
          });
        }
      });
    }
  });

  return snippets;
};

export const createAttachment = (
  type: Attachment['type'],
  content: string,
  name?: string
): Attachment => {
  return {
    id: generateId(),
    type,
    content,
    name,
  };
};

export const getDocumentFileSize = (content: string): number => {
  return new Blob([content]).size;
};

export const detectFileType = (filename: string): string => {
  const extension = filename.split('.').pop()?.toLowerCase();

  if (!extension) return 'markdown';

  switch (extension) {
    case 'txt':
      return 'text';
    case 'md':
      return 'markdown';
    case 'html':
      return 'html';
    case 'json':
      return 'json';
    case 'csv':
      return 'csv';
    default:
      return 'markdown';
  }
};
